import { Action, AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { normalize } from 'normalizr';

import EntityName from '../../enums/EntityName';
import EntityType from '../../types/Entity';
import RepositoryFactory from '../../repositories/RepositoryFactory';
import { ReduxThunkType } from '../../types/ReduxThunkType';
import { RestErrorResponse } from '../../types/response/RestErrorResponse';
import { generateEntityCreateActions, generateEntityGetActions, generateEntityGetListActions } from './generateEntityActions';
import { SchemasByEntityName } from '../schemas';
import { EntityMap } from '../../types/EntityMap';
import { RootState } from '../state';

const saveNestedEntities = (entities: any, rootEntityName: EntityName, dispatch: ThunkDispatch<RootState, null, Action<any>>) => {
  const otherEntities = Object.keys(entities).filter((name) => name !== rootEntityName) as EntityName[];

  otherEntities.forEach((nestedEntityName: EntityName) => {
    const normalizedEntityMap = entities[nestedEntityName] as EntityMap<EntityType>;
    const OtherEntityActions = generateEntityGetListActions(nestedEntityName);
    dispatch(OtherEntityActions.getListSuccess(Object.values(normalizedEntityMap)));
  });
};

export const generateEntityFetchListAsyncAction = <T extends EntityType>(entityName: EntityName) => {
  const repository = RepositoryFactory.get<T>(entityName);

  return (params: Record<string, string | number>): ReduxThunkType<void> =>
    async (dispatch) => {
      const EntityActions = generateEntityGetListActions(entityName);
      try {
        dispatch(EntityActions.getList());
        const response = await repository.getAll(params);
        dispatch(EntityActions.getListSuccess(response));
      } catch (error) {
        dispatch(EntityActions.getListError(error as RestErrorResponse));
      }
    };
};

export const generateEntityFetchAsyncAction = <T extends EntityType>(entityName: EntityName) => {
  const repository = RepositoryFactory.get<T>(entityName);
  const normalizrSchema = SchemasByEntityName[entityName];

  return (id: number): ReduxThunkType<void> =>
    async (dispatch) => {
      const EntityActions = generateEntityGetActions(entityName);

      try {
        dispatch(EntityActions.get());
        const response = await repository.get(id);
        if (normalizrSchema) {
          const { entities, result } = normalize(response, normalizrSchema);
          saveNestedEntities(entities, entityName, dispatch);

          const normalizedRootEntity = (entities[entityName] as EntityMap<T>)[result];
          dispatch(EntityActions.getSuccess(normalizedRootEntity));
        } else {
          dispatch(EntityActions.getSuccess(response));
        }
      } catch (error) {
        dispatch(EntityActions.getError(error as RestErrorResponse));
      }
    };
};

export const generateEntityCreateAsyncAction = <T extends EntityType>(entityName: EntityName) => {
  const repository = RepositoryFactory.get<T>(entityName);
  const normalizrSchema = SchemasByEntityName[entityName];

  return (entity: Partial<T>): ReduxThunkType<Promise<string>> =>
    async (dispatch) => {
      const EntityActions = generateEntityCreateActions(entityName);

      try {
        dispatch(EntityActions.create(entity));
        const response = await repository.create(entity);
        if (normalizrSchema) {
          const { entities, result } = normalize(response, normalizrSchema);
          saveNestedEntities(entities, entityName, dispatch);

          const normalizedRootEntity = (entities[entityName] as EntityMap<T>)[result];
          dispatch(EntityActions.createSuccess(normalizedRootEntity));
        } else {
          dispatch(EntityActions.createSuccess(response));
        }
        return Promise.resolve(response.id);
      } catch (error) {
        const response: AnyAction = dispatch(EntityActions.createError(error as RestErrorResponse));
        return Promise.resolve(response.payload.data.result.errorMessage);
      }
    };
};
